// Mock data for Italian Insiders website
// Business Model: Free basics → Paid full guide → Premium custom itinerary

export const heroData = {
  title: "Discover Italy's best kept secrets",
  subtitle: "from beginning to end",
  description: "Skip the overwhelming guides. Get curated, insider recommendations for what to see, where to eat, and hidden gems only locals know.",
  location: "Amalfi Coast, Italy",
  backgroundImage: "/images/amalfi-coast.jpg"
};

export const valueProposition = {
  problem: "Planning a trip to Italy shouldn't feel like a second job.",
  description: "TripAdvisor lists everything. Viator sells everything. Travel blogs bury the good stuff. We cut through the noise with clear, curated recommendations from someone who actually lives here.",
  benefits: [
    "No endless scrolling through reviews",
    "No sponsored listings or paid placements", 
    "Just honest, local picks you can trust"
  ]
};

export const aboutData = {
  title: "I got tired of friends asking 'where should I eat in Rome?' and sending them 47 links.",
  description: "Every time someone visits Italy, they're overwhelmed. Should they book that €200 Vatican tour? Is that restaurant on the main square actually good? What are locals doing that tourists miss?",
  secondaryDescription: "Italian Insiders is my answer: simple, curated guides that give you a local's perspective. The places I'd actually take my friends—not everything, just the good stuff.",
  cta: "See Our Destinations",
  image: "/images/venice-canal.jpg",
  location: "Venice, Italy"
};

// Updated to 6 specific destinations
export const cities = [
  {
    id: 1,
    name: "Rome",
    slug: "rome",
    tagline: "The Eternal City",
    description: "Ancient history meets vibrant street life. Skip the tourist traps, find the real Rome.",
    image: "/images/rome.jpg",
    freeHighlights: ["Colosseum", "Vatican", "Trevi Fountain"],
    paidHighlights: ["Best carbonara spots", "Hidden viewpoints", "Aperitivo like a local"],
    guidePrice: "$199",
    itineraryPrice: "from $499"
  },
  {
    id: 2,
    name: "Florence",
    slug: "florence",
    tagline: "Cradle of the Renaissance",
    description: "World-class art and authentic Tuscan food, without fighting through crowds.",
    image: "/images/florence-new.jpg",
    freeHighlights: ["Uffizi Gallery", "Ponte Vecchio", "Duomo"],
    paidHighlights: ["Secret gardens", "Artisan workshops", "Wine bars locals love"],
    guidePrice: "$199",
    itineraryPrice: "from $499"
  },
  {
    id: 3,
    name: "Venice",
    slug: "venice",
    tagline: "The Floating City",
    description: "Beyond St. Mark's Square—discover the Venice that still belongs to Venetians.",
    image: "/images/venice-gondola.jpg",
    freeHighlights: ["St. Mark's Basilica", "Rialto Bridge", "Grand Canal"],
    paidHighlights: ["Quiet neighborhood bars", "Best cicchetti spots", "Escape the crowds"],
    guidePrice: "$199",
    itineraryPrice: "from $499"
  },
  {
    id: 4,
    name: "The Dolomites",
    slug: "dolomites",
    tagline: "Alpine Paradise",
    description: "Dramatic peaks, pristine lakes, and mountain huts with views that don't quit.",
    image: "/images/dolomites.jpg",
    freeHighlights: ["Tre Cime", "Lake Braies", "Seceda"],
    paidHighlights: ["Best rifugios", "Hidden trails", "Where to base yourself"],
    guidePrice: "$199",
    itineraryPrice: "from $499"
  },
  {
    id: 5,
    name: "Cinque Terre",
    slug: "cinque-terre",
    tagline: "Five Colorful Villages",
    description: "Yes, it's touristy. Here's how to experience the magic anyway.",
    image: "/images/cinque-terre.jpg",
    freeHighlights: ["Manarola", "Vernazza", "Coastal Trail"],
    paidHighlights: ["When to visit each village", "Hidden beaches", "Pesto worth the hike"],
    guidePrice: "$199",
    itineraryPrice: "from $499"
  },
  {
    id: 6,
    name: "Amalfi Coast",
    slug: "amalfi-coast",
    tagline: "Mediterranean Paradise",
    description: "Cliffside villages and azure waters. Know where to go (and what to skip).",
    image: "/images/amalfi-citta.jpg",
    freeHighlights: ["Positano", "Ravello", "Amalfi"],
    paidHighlights: ["Beaches without crowds", "Family trattorias", "Best lemon granita"],
    guidePrice: "$199",
    itineraryPrice: "from $499"
  }
];

// Pricing tiers
export const pricingTiers = [
  {
    id: "free",
    name: "Free Basics",
    price: "$0",
    description: "Get started with the essentials",
    features: [
      "Must-see attractions overview",
      "Best times to visit",
      "Basic neighborhood guide",
      "Getting around tips"
    ],
    cta: "Start Reading",
    highlighted: false
  },
  {
    id: "guide",
    name: "Full City Guide",
    price: "$199",
    period: "one-time",
    description: "Everything you need for an amazing trip",
    features: [
      "All free content, plus:",
      "10+ restaurant recommendations",
      "Hidden gems & local secrets",
      "Off-the-beaten-path spots",
      "Neighborhood deep-dives",
      "Day trip suggestions",
      "Practical tips & timing"
    ],
    cta: "Get the Guide",
    highlighted: true
  },
  {
    id: "itinerary",
    name: "Custom Itinerary",
    price: "from $499",
    period: "",
    description: "Your perfect trip, planned for you",
    features: [
      "Everything in Full Guide, plus:",
      "Personalized day-by-day plan",
      "Based on your dates & interests",
      "Restaurant reservations advice",
      "Optimal route planning",
      "Email support for questions"
    ],
    cta: "Get Started",
    highlighted: false
  }
];

export const experiences = [
  {
    id: 1,
    name: "Where to Eat",
    slug: "where-to-eat",
    description: "From neighborhood trattorias to that perfect espresso spot",
    icon: "utensils",
    image: "/images/carbonara.jpg"
  },
  {
    id: 2,
    name: "What to See",
    slug: "what-to-see",
    description: "The famous stuff plus hidden gems most tourists miss",
    icon: "camera",
    image: "/images/train.jpg"
  },
  {
    id: 3,
    name: "Off the Path",
    slug: "off-the-path",
    description: "Quieter spots, local neighborhoods, the real Italy",
    icon: "compass",
    image: "/images/toscana.jpg"
  },
  {
    id: 4,
    name: "Practical Tips",
    slug: "practical-tips",
    description: "When to go, how to get there, what to book ahead",
    icon: "lightbulb",
    image: "/images/gondola.jpg"
  }
];

export const howItWorks = [
  {
    id: 1,
    title: "Pick your destination",
    description: "Choose from Rome, Florence, Venice, the Dolomites, Cinque Terre, or the Amalfi Coast.",
    icon: "map-pin"
  },
  {
    id: 2,
    title: "Read the free basics",
    description: "Get an overview of must-sees and best times to visit—completely free.",
    icon: "book-open"
  },
  {
    id: 3,
    title: "Go deeper if you want",
    description: "Unlock the full guide for restaurant picks, hidden gems, and local secrets. Or get a custom itinerary built just for you.",
    icon: "key"
  }
];

export const testimonials = [
  {
    id: 1,
    quote: "Finally, recommendations I can actually use. No scrolling through 500 TripAdvisor reviews—just 'go here, order this.' Perfect.",
    author: "Sarah M.",
    location: "San Francisco",
    trip: "Rome Guide"
  },
  {
    id: 2,
    quote: "The restaurant list alone was worth it. We ate better in Florence than we have anywhere, ever. Not a single tourist trap.",
    author: "James & Kate",
    location: "London",
    trip: "Florence Guide"
  },
  {
    id: 3,
    quote: "I usually over-plan everything. This guide gave me permission to just... enjoy. Everything was already figured out.",
    author: "Michael T.",
    location: "Toronto",
    trip: "Custom Itinerary"
  }
];

export const faqs = [
  {
    question: "What makes this different from TripAdvisor or travel blogs?",
    answer: "No algorithms, no sponsored content, no 'top 50 lists.' Just one person's honest picks—the places I'd send my own friends. It's curated, not comprehensive."
  },
  {
    question: "How often is the content updated?",
    answer: "I review and update each guide at least twice a year, and whenever I discover something new worth sharing. Restaurants close, new gems open—the guides stay current."
  },
  {
    question: "What's included in the free section?",
    answer: "A solid overview of each destination: the main attractions, best times to visit, neighborhood orientation, and basic getting-around tips. Enough to get excited and start planning."
  },
  {
    question: "Is the custom itinerary really personalized?",
    answer: "Yes—I read your preferences, dates, and travel style, then build a day-by-day plan specifically for you. It's not a template. I usually deliver within 3-5 days."
  },
  {
    question: "Can I get a refund?",
    answer: "If the guide doesn't help you plan a better trip, email me within 7 days for a full refund. No questions asked."
  }
];

export const footerLinks = {
  destinations: [
    { name: "Rome", href: "/city/rome" },
    { name: "Florence", href: "/city/florence" },
    { name: "Venice", href: "/city/venice" },
    { name: "The Dolomites", href: "/city/dolomites" },
    { name: "Cinque Terre", href: "/city/cinque-terre" },
    { name: "Amalfi Coast", href: "/city/amalfi-coast" }
  ],
  guides: [
    { name: "Where to Eat", href: "/how-it-works" },
    { name: "What to See", href: "/how-it-works" },
    { name: "Off the Path", href: "/how-it-works" },
    { name: "Practical Tips", href: "/how-it-works" }
  ],
  company: [
    { name: "About", href: "/about" },
    { name: "How It Works", href: "/how-it-works" },
    { name: "FAQ", href: "/faq" },
    { name: "Contact", href: "/contact" }
  ],
  legal: [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" }
  ]
};

export const socialLinks = [
  { name: "Instagram", href: "https://instagram.com", icon: "instagram" },
  { name: "Email", href: "mailto:info@italianinsiders.com", icon: "mail" }
];

// Sample free content for Rome (preview)
export const sampleFreeContent = {
  rome: {
    overview: "Rome is overwhelming in the best way. Two thousand years of history layered on top of each other, incredible food around every corner, and that golden evening light that makes everything look like a movie set.",
    bestTime: "April-May or September-October. Summer is hot and packed. Winter is mild but some things close.",
    neighborhoods: [
      { name: "Centro Storico", vibe: "The historic heart. Touristy but essential." },
      { name: "Trastevere", vibe: "Cobblestone charm, great restaurants, lively at night." },
      { name: "Testaccio", vibe: "Working-class roots, serious food scene." },
      { name: "Monti", vibe: "Artsy, boutiques, wine bars." }
    ],
    mustSee: [
      "Colosseum (book tickets in advance)",
      "Vatican Museums & St. Peter's (go early or late)",
      "Pantheon (free, go at opening)",
      "Roman Forum",
      "Trastevere neighborhood walk"
    ]
  },
  florence: {
    overview: "Florence is the birthplace of the Renaissance. Art, architecture, and incredible food in a compact, walkable city.",
    bestTime: "April-June or September-October. July-August is hot and crowded.",
    neighborhoods: [
      { name: "Centro Storico", vibe: "Historic heart. Museums, Duomo, main attractions." },
      { name: "Oltrarno", vibe: "Artisan quarter. Quieter, authentic workshops and trattorias." },
      { name: "Santa Croce", vibe: "Local nightlife. Markets, bars, fewer tourists." },
      { name: "San Lorenzo", vibe: "Market central. Leather goods and street food." }
    ],
    mustSee: [
      "Uffizi Gallery (book in advance)",
      "Duomo and climb the dome",
      "Accademia Gallery (David statue)",
      "Ponte Vecchio",
      "Boboli Gardens"
    ]
  },
venice: {
    overview: "Venice is impossible. A city built on water, no cars, just boats and footbridges. Yes, it's crowded. Yes, it's worth it.",
    bestTime: "November-March for fewer crowds. April-June is beautiful but packed. Avoid summer unless you love heat and crowds.",
    neighborhoods: [
      { name: "San Marco", vibe: "Tourist central. Piazza, basilica, Doge's Palace. Go early or late." },
      { name: "Dorsoduro", vibe: "Art district. Museums, galleries, quieter canals, university energy." },
      { name: "Cannaregio", vibe: "Local life. Jewish ghetto, authentic restaurants, real Venetians." },
      { name: "Castello", vibe: "Eastern quiet zone. Residential, less touristy, great for wandering." }
    ],
    mustSee: [
      "St. Mark's Basilica (go at opening)",
      "Doge's Palace",
      "Rialto Bridge and Market",
      "Get lost in the back canals",
      "Take vaporetto down Grand Canal"
    ]
  },

  dolomites: {
    overview: "Jagged peaks, alpine meadows, and mountain towns that look like postcards. Summer hiking, winter skiing, year-round beauty.",
    bestTime: "June-September for hiking. December-March for skiing. Shoulder seasons (May, October) for fewer crowds.",
    neighborhoods: [
      { name: "Cortina d'Ampezzo", vibe: "Upscale ski town. Luxury hotels, high-end shopping, Olympic history." },
      { name: "Val Gardena", vibe: "Ladin culture. Great skiing, alpine villages, local traditions." },
      { name: "Tre Cime", vibe: "Iconic peaks. Day hikes, mountain huts, dramatic views." },
      { name: "Lake Braies", vibe: "Turquoise waters. Famous lake, boat rentals, easy access." }
    ],
    mustSee: [
      "Tre Cime di Lavaredo hike",
      "Seceda viewpoint (cable car up)",
      "Lake Braies at sunrise",
      "Drive the Great Dolomites Road",
      "Traditional mountain rifugio lunch"
    ]
  },

  "cinque-terre": {
    overview: "Five colorful villages clinging to cliffs above the Mediterranean. Hiking trails, fresh seafood, limoncello with a view.",
    bestTime: "April-May or September-October. Summer is packed. Winter is quiet but some places close.",
    neighborhoods: [
      { name: "Monterosso", vibe: "Biggest village. Actual beach, more restaurants, easier to navigate." },
      { name: "Vernazza", vibe: "Most picturesque. Harbor views, steep streets, postcard perfect." },
      { name: "Corniglia", vibe: "Hilltop quiet. No beach, fewer crowds, locals still live here." },
      { name: "Manarola", vibe: "Sunset spot. Cliffside swimming, famous viewpoint, romantic." }
    ],
    mustSee: [
      "Hike Monterosso to Vernazza (best trail)",
      "Swim at Manarola rocks",
      "Sunset at Riomaggiore harbor",
      "Fresh pesto and focaccia everywhere",
      "Take the train between villages"
    ]
  },

  "amalfi-coast": {
    overview: "Dramatic coastline, lemon groves, and villages stacked on cliffs. Beautiful, expensive, and absolutely worth seeing once.",
    bestTime: "April-June or September-October. July-August is hot, crowded, and pricey. Winter is quiet but some places close.",
    neighborhoods: [
      { name: "Positano", vibe: "Instagram central. Expensive, gorgeous, vertical streets, beach clubs." },
      { name: "Amalfi Town", vibe: "Historic center. Cathedral, shops, less expensive than Positano." },
      { name: "Ravello", vibe: "Hilltop elegance. Gardens, classical music, views without beach crowds." },
      { name: "Praiano", vibe: "Quieter alternative. Fewer tourists, authentic restaurants, local vibe." }
    ],
    mustSee: [
      "Drive or bus the coastal road",
      "Villa Cimbrone gardens in Ravello",
      "Beach day in Positano (expensive but worth it)",
      "Path of the Gods hike",
      "Fresh limoncello at every meal"
    ]
  },
};

// Sample paid content preview (teaser)
export const samplePaidPreview = {
  rome: {
    restaurants: [
      { name: "Da Enzo al 29", type: "Traditional Roman", why: "The carbonara that ruined all other carbonaras for me." },
      { name: "Suppli", type: "Street Food", why: "Perfect fried rice balls. Get the classic ragù." },
      { name: "...", type: "7 more in full guide", why: "" }
    ],
    hiddenGems: [
      { name: "Aventine Keyhole", what: "Secret view of St. Peter's through a door" },
      { name: "Quartiere Coppede", what: "Fairytale architecture nobody visits" },
      { name: "...", what: "12 more in full guide" }
    ]
  },
  florence: {
    restaurants: [
      { name: "Trattoria Mario", type: "Traditional Tuscan", why: "No-frills lunch spot. Locals only. Cash only. Worth it." },
      { name: "All'Antico Vinaio", type: "Sandwiches", why: "Legendary schiacciata. Expect a line. Get the truffle one." },
      { name: "...", type: "7 more in full guide", why: "" }
    ],
    hiddenGems: [
      { name: "San Miniato al Monte", what: "Best sunset view of Florence. Free. Quiet." },
      { name: "Mercato di Sant'Ambrogio", what: "Real local market. No tourists. Great porchetta." },
      { name: "...", what: "12 more in full guide" }
    ]
  },
  venice: {
    restaurants: [
      { name: "Osteria Bancogiro", type: "Canalside", why: "Rialto market views. Cicchetti and spritz. Locals approved." },
      { name: "Dai Zemei", type: "Bacaro", why: "Tiny wine bar. Standing room only. Best cicchetti in Venice." },
      { name: "...", type: "7 more in full guide", why: "" }
    ],
    hiddenGems: [
      { name: "Libreria Acqua Alta", what: "Bookshop with books in bathtubs. Cats everywhere. Instagram gold." },
      { name: "Fondaco dei Tedeschi rooftop", what: "Free 360° views. Book online. Better than Campanile." },
      { name: "...", what: "12 more in full guide" }
    ]
  },

  dolomites: {
    restaurants: [
      { name: "Rifugio Lagazuoi", type: "Mountain Hut", why: "Cable car up. Lunch with views. Traditional speck and polenta." },
      { name: "Malga Ciapela", type: "Alpine Farm", why: "End of Marmolada road. Cheese made on-site. Simple, perfect." },
      { name: "...", type: "7 more in full guide", why: "" }
    ],
    hiddenGems: [
      { name: "Lago di Carezza", what: "Rainbow lake. Early morning light. Worth the drive from anywhere." },
      { name: "Alpe di Siusi", what: "Europe's largest alpine meadow. Wildflowers, cow bells, peace." },
      { name: "...", what: "12 more in full guide" }
    ]
  },

  "cinque-terre": {
    restaurants: [
      { name: "Nessun Dorma", type: "Wine Bar", why: "Manarola sunset views. Cheese plates. Get there early for a table." },
      { name: "Ristorante Belforte", type: "Cliffside", why: "Built into the rocks. Seafood. Vernazza's best splurge." },
      { name: "...", type: "7 more in full guide", why: "" }
    ],
    hiddenGems: [
      { name: "Guvano Beach", what: "Abandoned tunnel beach. Clothing optional. Locals only know." },
      { name: "Corniglia train station hike", what: "382 steps up. Skip the bus. Earn your limoncello." },
      { name: "...", what: "12 more in full guide" }
    ]
  },

  "amalfi-coast": {
    restaurants: [
      { name: "Da Adolfo", type: "Beach Restaurant", why: "Boat from Positano. Fresh fish, mozzarella. Bring cash." },
      { name: "Trattoria da Ciccio", what: "Amalfi locals' spot. No tourists. Real prices. Real food." },
      { name: "...", type: "7 more in full guide", why: "" }
    ],
    hiddenGems: [
      { name: "Fiordo di Furore", what: "Hidden fjord beach. Tiny, dramatic, under a bridge." },
      { name: "Atrani", what: "Next to Amalfi but nobody goes. Real town, real life, no crowds." },
      { name: "...", what: "12 more in full guide" }
    ]
  }
};
